import merge from 'lodash/merge';
import { REPORT_TYPE } from '@/configs/info';
import qs from 'qs';

export const ERROR_CATEGORY = {
  AJAX: 'ajaxError',
  JS: 'jsError',
  RESOURCE: 'resourceError',
  BUSINESS: 'businessError',
  UPLOAD: 'uploadError',
};

export const ERROR_LEVEL = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug',
};

export const initOwl = currentUser => {
  window.owl('setDimension', { unionId: currentUser.id });
  // window.owl('config', { pageUrl: 1 });
};

/**
 * @typedef ErrorOptions
 * @property {ERROR_CATEGORY.JS | ERROR_CATEGORY.RESOURCE | ERROR_CATEGORY.AJAX | ERROR_CATEGORY.BUSINESS} category
 * @property {ERROR_LEVEL.INFO | ERROR_LEVEL.WARN | ERROR_LEVEL.ERROR | ERROR_LEVEL.DEBUG} level
 * @property {Record<string, string | number>} [tags]
 * @property {boolean} [combo=true]
 */

/**
 * @desc 上报日志到 Raptor 异常分析
 * @param {Error|string|{msg?: string, name: string}} error
 * @param {Partial<ErrorOptions>} opts
 */
export const reportError = (error, opts) => {
  window.owl(
    'addError',
    error,
    merge(
      {
        level: ERROR_LEVEL.ERROR,
        category: ERROR_CATEGORY.JS,
      },
      opts
    )
  );
};

export const uploadError = (error, opts) => {
  reportError(
    error,
    merge(
      {
        category: ERROR_CATEGORY.UPLOAD,
      },
      opts
    )
  );
};

/**
 * @desc 上报 ajax error
 * @param {string} url
 * @param {Partial<ErrorOptions>} opts
 */
export const ajaxError = (url, opts) => {
  reportError(
    url,
    merge(
      {
        category: ERROR_CATEGORY.AJAX,
      },
      opts
    )
  );
};

/**
 * @desc 上报业务异常
 * @param {Error} error
 * @param {Partial<ErrorOptions>} opts
 */
export const businessError = (error, opts) => {
  reportError(error, merge({ category: ERROR_CATEGORY.BUSINESS }, opts));
};

/**
 * @typedef ApiOptions
 * @property {string} name 请求路径
 * @property {number} networkCode HTTP 状态码
 * @property {number} statusCode 业务状态码
 * @property {number} responseTime 请求耗时
 * @property {string} [content] 当触发异常时作为异常堆栈
 */

/**
 * @desc 上报 API 日志到 Raptor 请求分析
 * @param {string }url
 * @param {Object} request
 * @param {Response} response
 * @param {number} responseTime
 */
export const reportApi = async (url, request, response, responseTime) => {
  let apiPath = url;
  let apiSearch = null;
  if (url.includes('?')) {
    const temp = url.split('?');
    apiPath = temp[0];
    apiSearch = qs.parse(temp[1]) || null;
  }

  const networkCode = response.status;
  const responseData = response.status === 204 ? await response.text() : await response.json();
  const statusCode = responseData?.code || responseData?.status || null;

  const apiOptions = {
    name: url.startsWith('//') ? 'https:' + url : url,
    networkCode,
    statusCode,
    responseTime,
  };

  if (apiPath.includes(window.location.hostname))
    apiPath = apiPath.replace(window.location.hostname, '');
  if (networkCode !== 200 || statusCode !== 200) {
    ajaxError(`${apiPath} code=${statusCode}`, {
      level: !statusCode || statusCode === 500 ? ERROR_LEVEL.ERROR : ERROR_LEVEL.WARN,
      tags: {
        level1: apiPath,
        level2: statusCode,
        url,
        responseData,
        responseTime,
        query: apiSearch,
        body: request?.body,
      },
    });
  }

  window.owl('addApi', apiOptions);
};

/**
 * @desc 重新上报一次 PV，用于 SPA 切换路由
 */
export const reportPv = () => {
  window.owl('resetPv');
};

/**
 * @desc 上报打点到 Raptor 自定义指标（MetricInst 单次使用）
 * @param {string} name 指标名称
 * @param {number} value 指标打点，一般为耗时数据
 * @param {Record<string, string>} tags 指标携带的维度数据
 */
export const reportMetric = (name, value, tags) => {
  const metricInst = window.owl('newMetricInst');
  if (metricInst) {
    metricInst.setTags(tags);
    metricInst.setMetric(name, value);
  }
};

/**
 * @desc 创建一个可复用的 metric 实例
 */
export const createMetric = () => {
  return window.owl('newMetricInst');
};
